import React from 'react';
import { Activity, AlertCircle, Archive, CheckCircle, Clock, FileText, RefreshCw, TrendingUp, UserCheck, Users, XCircle } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Progress } from '../../../components/ui/progress';
import { TabsContent } from '../../../components/ui/tabs';
import { StatCard } from '../StatCard';

export const AnalyticsTab = ({ activeUsers, analytics, loadActiveUsers, loadingActiveUsers, loadingAnalytics }) => (
            <TabsContent value="analytics">
              {loadingAnalytics && !analytics ? (
                <div className="text-center py-12 text-muted-foreground">
                  Loading analytics...
                </div>
              ) : analytics ? (
                <div className="space-y-6">
                  {/* Active Users Banner */}
                  <Card className="border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50">
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-6">
                          <div className="flex items-center gap-2">
                            <div className="relative">
                              <Users className="w-8 h-8 text-emerald-600" />
                              <span className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-500 rounded-full animate-pulse"></span>
                            </div>
                            <div>
                              <p className="text-sm text-muted-foreground">Online Now</p>
                              <p className="text-2xl font-bold text-emerald-700">
                                {loadingActiveUsers ? '...' : (activeUsers?.online_now || 0)}
                              </p>
                            </div>
                          </div>
                          <div className="h-10 w-px bg-slate-200"></div>
                          <div>
                            <p className="text-sm text-muted-foreground">Active (15 min)</p>
                            <p className="text-xl font-semibold text-slate-700">
                              {loadingActiveUsers ? '...' : (activeUsers?.active_15min || 0)}
                            </p>
                          </div>
                          <div className="h-10 w-px bg-slate-200"></div>
                          <div>
                            <p className="text-sm text-muted-foreground">Active (1 hour)</p>
                            <p className="text-xl font-semibold text-slate-700">
                              {loadingActiveUsers ? '...' : (activeUsers?.active_1hour || 0)}
                            </p>
                          </div>
                        </div>
                        <Button variant="ghost" size="sm" onClick={loadActiveUsers} disabled={loadingActiveUsers}>
                          <RefreshCw className={`w-4 h-4 ${loadingActiveUsers ? 'animate-spin' : ''}`} />
                        </Button>
                      </div>
                      {activeUsers?.active_users?.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-emerald-200">
                          <p className="text-xs text-muted-foreground mb-2">Currently Online:</p>
                          <div className="flex flex-wrap gap-2">
                            {activeUsers.active_users.slice(0, 8).map((user) => (
                              <span key={user.id || user.email} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-white border border-emerald-200">
                                <span className="w-2 h-2 bg-emerald-500 rounded-full mr-2"></span>
                                {user.display_name || user.email}
                              </span>
                            ))}
                            {activeUsers.active_users.length > 8 && (
                              <span className="text-xs text-muted-foreground">+{activeUsers.active_users.length - 8} more</span>
                            )}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
  
                  {/* Overview Stats */}
                  <div>
                    <h2 className="text-lg font-semibold mb-4">Overview</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <StatCard
                        title="Total Users"
                        value={analytics.overview.total_users}
                        icon={Users}
                      />
                      <StatCard
                        title="Active Tacticals"
                        value={analytics.overview.active_tacticals}
                        subtitle={`${analytics.overview.participation_rate}% participation`}
                        icon={FileText}
                      />
                      <StatCard
                        title="Archived Tacticals"
                        value={analytics.overview.archived_tacticals}
                        icon={Archive}
                      />
                      <StatCard
                        title="Total Tacticals"
                        value={analytics.overview.total_tacticals}
                        icon={TrendingUp}
                      />
                    </div>
                  </div>
  
                  {/* Participation & Manager Stats */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Participation */}
                    <Card className="border-slate-200">
                      <CardHeader>
                        <CardTitle className="text-base flex items-center gap-2">
                          <UserCheck className="w-5 h-5 text-primary" />
                          Participation Rate
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm text-muted-foreground">Users with Active Tactical</span>
                              <span className="font-semibold">{analytics.overview.users_with_active_tactical} / {analytics.overview.total_users}</span>
                            </div>
                            <Progress value={analytics.overview.participation_rate} className="h-3" />
                            <p className="text-xs text-muted-foreground mt-1">{analytics.overview.participation_rate}% of users have an active tactical</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
  
                    {/* Manager Stats */}
                    <Card className="border-slate-200">
                      <CardHeader>
                        <CardTitle className="text-base flex items-center gap-2">
                          <Users className="w-5 h-5 text-primary" />
                          Manager Relationships
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-3 gap-4 text-center">
                          <div>
                            <p className="text-2xl font-bold">{analytics.manager_stats.total_relationships}</p>
                            <p className="text-xs text-muted-foreground">Approved</p>
                          </div>
                          <div>
                            <p className="text-2xl font-bold">{analytics.manager_stats.pending_requests}</p>
                            <p className="text-xs text-muted-foreground">Pending</p>
                          </div>
                          <div>
                            <p className="text-2xl font-bold">{analytics.manager_stats.unique_managers}</p>
                            <p className="text-xs text-muted-foreground">Managers</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
  
                  {/* Scorecard Status */}
                  <Card className="border-slate-200">
                    <CardHeader>
                      <CardTitle className="text-base">Scorecard Status Distribution</CardTitle>
                      <CardDescription>Status breakdown across all active tacticals</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                            <CheckCircle className="w-6 h-6 text-green-600" />
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-green-600">{analytics.scorecard.totals.green}</p>
                            <p className="text-sm text-muted-foreground">Green ({analytics.scorecard.percentages.green}%)</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center">
                            <AlertCircle className="w-6 h-6 text-yellow-600" />
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-yellow-600">{analytics.scorecard.totals.yellow}</p>
                            <p className="text-sm text-muted-foreground">Yellow ({analytics.scorecard.percentages.yellow}%)</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                            <XCircle className="w-6 h-6 text-red-600" />
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-red-600">{analytics.scorecard.totals.red}</p>
                            <p className="text-sm text-muted-foreground">Red ({analytics.scorecard.percentages.red}%)</p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
  
                  {/* Monthly Activity & Facility Completion */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Monthly Activity */}
                    <Card className="border-slate-200">
                      <CardHeader>
                        <CardTitle className="text-base flex items-center gap-2">
                          <TrendingUp className="w-5 h-5 text-primary" />
                          Monthly Activity
                        </CardTitle>
                        <CardDescription>Tacticals created per month</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {analytics.monthly_activity.map((month, idx) => (
                            <div key={idx} className="flex items-center gap-3">
                              <span className="text-sm text-muted-foreground w-20">{month.month}</span>
                              <div className="flex-1 bg-slate-100 rounded-full h-4 overflow-hidden">
                                <div 
                                  className="bg-primary h-full rounded-full transition-all"
                                  style={{ 
                                    width: `${Math.min(100, (month.count / Math.max(...analytics.monthly_activity.map(m => m.count), 1)) * 100)}%` 
                                  }}
                                />
                              </div>
                              <span className="text-sm font-medium w-8 text-right">{month.count}</span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
  
                    {/* Facility Completion */}
                    <Card className="border-slate-200">
                      <CardHeader>
                        <CardTitle className="text-base flex items-center gap-2">
                          <FileText className="w-5 h-5 text-primary" />
                          Facility Overview Completion
                        </CardTitle>
                        <CardDescription>How many users have filled out their facility items</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-3 gap-4 text-center">
                          <div className="p-4 bg-green-50 rounded-lg">
                            <p className="text-2xl font-bold text-green-600">{analytics.facility_completion.complete}</p>
                            <p className="text-xs text-muted-foreground">Complete</p>
                          </div>
                          <div className="p-4 bg-yellow-50 rounded-lg">
                            <p className="text-2xl font-bold text-yellow-600">{analytics.facility_completion.partial}</p>
                            <p className="text-xs text-muted-foreground">Partial</p>
                          </div>
                          <div className="p-4 bg-slate-50 rounded-lg">
                            <p className="text-2xl font-bold text-slate-600">{analytics.facility_completion.empty}</p>
                            <p className="text-xs text-muted-foreground">Empty</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
  
                  {/* Recent Users & Top Contributors */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Recent Users */}
                    <Card className="border-slate-200">
                      <CardHeader>
                        <CardTitle className="text-base flex items-center gap-2">
                          <Clock className="w-5 h-5 text-primary" />
                          Recent Logins
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {analytics.recent_users.slice(0, 5).map((user) => (
                            <div key={user.id || user.email} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                              <div>
                                <p className="text-sm font-medium">{user.display_name}</p>
                                <p className="text-xs text-muted-foreground">{user.email}</p>
                              </div>
                              <span className="text-xs text-muted-foreground">
                                {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'N/A'}
                              </span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
  
                    {/* Top Contributors */}
                    <Card className="border-slate-200">
                      <CardHeader>
                        <CardTitle className="text-base flex items-center gap-2">
                          <Archive className="w-5 h-5 text-primary" />
                          Top Contributors
                        </CardTitle>
                        <CardDescription>Users with most archived tacticals</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {analytics.top_users.length > 0 ? (
                            analytics.top_users.map((user, idx) => (
                              <div key={user.id || user.email || idx} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                                <div className="flex items-center gap-3">
                                  <span className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">
                                    {idx + 1}
                                  </span>
                                  <div>
                                    <p className="text-sm font-medium">{user.name}</p>
                                    <p className="text-xs text-muted-foreground">{user.email}</p>
                                  </div>
                                </div>
                                <span className="text-sm font-semibold">{user.archived_count} tacticals</span>
                              </div>
                            ))
                          ) : (
                            <p className="text-sm text-muted-foreground text-center py-4">No archived tacticals yet</p>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
  
                  {/* Generated timestamp */}
                  <p className="text-xs text-muted-foreground text-center">
                    Last updated: {new Date(analytics.generated_at).toLocaleString()}
                  </p>
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  Failed to load analytics. Click refresh to try again.
                </div>
              )}
            </TabsContent>
);
